import { SERVICE_TYPE_ONTRUCK24 } from 'modules/shipper-order/constants';
import { TENANTS_NAMES } from 'tenant/host_config';
import { getTenantPreLoginConfig } from 'tenant/teanantPreLoginConfig';

const SHIPMENTS_FIELD = 'shipments';
const EXTRAS_FIELD = 'extras';
const NUMBER_OF_ITEMS_FIELD = 'number_of_items';
const INSURED_FOR_FIELD = 'insured_for';
const TOTAL_WEIGHT_FIELD = 'total_weight';
const TEMPERATURE_RANGE_FIELD = 'temperature_range';
const LOADS_FIELD = 'loads';
const LOAD_TYPE_SWITCH_FIELD = 'load_type_switch';
const LOAD_TYPE_FIELD = 'load_type';
const PALLET_TYPE_FIELD = 'pallet_type';
const ITEM_WIDTH_FIELD = 'item_width';
const ITEM_LENGTH_FIELD = 'item_length';
const ITEM_HEIGHT_FIELD = 'item_height';
const INSURANCE_FIELD = 'insured_for';
const SHIPPER_ORDER_TYPE = 'shipper_order_type';
const STANDSTILL_FIELD = 'standstills';

const INSURANCE_EXTRA = 'INSURANCE';
const DAY_HOLIDAY_EXTRA = 'DAY_HOLIDAY';
const PRICE_INCREMENT_EXTRA = 'PRICE_INCREMENT';
const DISCOUNT_EXTRA = 'DISCOUNT';
const STANDSTILL_EXTRA = 'STANDSTILL';
const LIFGATE_EXTRA = 'LIFTGATE';
const TOP_LOAD_EXTRA = 'TOP_LOAD';
const SIDE_LOAD_EXTRA = 'SIDE_LOAD';
const MANUAL_EXTRA = 'MANUAL';
const MANAGEMENT_FEE_EXTRA = 'MANAGEMENT_FEE';
const TENDER_EXTRA = 'TENDER';
const NEGOTIATED_EXTRA = 'NEGOTIATED';
const CANCELLATION_EXTRA = 'CANCELLATION';
const PENALTY_EXTRA = 'PENALTY';
const BONUS_EXTRA = 'BONUS';
const MODIFIED_PRICE_EXTRA = 'MODIFIED_PRICE';
const RETURN_EXTRA = 'RETURN_SO';
const COST_INCREMENT_EXTRA = 'COST_INCREMENT';
const HANDLING_LOADING = 'HANDLING_LOADING';
const HANDLING_UNLOADING = 'HANDLING_UNLOADING';

const DISTRIBUTION = 'DISTRIBUTION';
const SINGLE_SHIPMENT = 'SINGLE_SHIPMENT';
const COLLECTION = 'COLLECTION';

const EXTRA_PERCENT = 'PERCENT';
const EXTRA_NUMERIC = 'NUMERIC';

const PENDING_CONFIRMATION = 'PENDING_CONFIRMATION';
const PENDING_PAYMENT = 'PENDING_PAYMENT';
const PENDING_MODERATION = 'PENDING_MODERATION';
const PENDING_ASSIGNMENT = 'PENDING_ASSIGNMENT';
const ASSIGNED = 'ASSIGNED';
const IN_TRANSIT = 'IN_TRANSIT';
const CANCELLED = 'CANCELLED';
const MIXED_MULTI_ROUTE = 'MIXED_MULTI_ROUTE';

const EXPIRED = 'EXPIRED';
const ACTIVE = 'ACTIVE';

const TRIGGER_FORM_CHANGE = 'initialized';
const HANDLING_EXTRAS = [HANDLING_LOADING, HANDLING_UNLOADING];

const formConfig = getTenantPreLoginConfig().create_order_form;

const includedInBaseList = [
  LIFGATE_EXTRA,
  SIDE_LOAD_EXTRA,
  PRICE_INCREMENT_EXTRA,
  COST_INCREMENT_EXTRA,
];

const pickupStopInfo = {
  addressSuffix: 'from',
  stopPreffix: 'pickup',
  handlingExtraName: 'HANDLING_LOADING',
};
const dropoffStopInfo = {
  addressSuffix: 'to',
  stopPreffix: 'dropoff',
  handlingExtraName: 'HANDLING_UNLOADING',
};

const stopInfo = {
  [COLLECTION]: pickupStopInfo,
  [DISTRIBUTION]: dropoffStopInfo,
};

const LOAD_TYPE_SWITCH = {
  PALLET: 'PALLET_BTN',
  NON_PALLETIZED: 'NON_PALLETIZED',
};

const LOAD_TYPES = {
  PALLET: 'PALLET',
  BULK: 'BULK',
  PARCEL: 'PARCEL',
};

const PALLET_TYPES = {
  EU_PALLET: 'EU_PALLET',
  US_PALLET: 'US_PALLET',
  CUSTOM_PALLET: 'CUSTOM_PALLET',
};

const NON_PALLETIZED_TYPES = {
  PARCEL: 'PARCEL',
  BULK: 'BULK',
};

const PALLET_SIZES = {
  [PALLET_TYPES.EU_PALLET]: {
    item_length: 120,
    item_width: 80,
  },
  [PALLET_TYPES.US_PALLET]: {
    item_length: 120,
    item_width: 100,
  },
  [PALLET_TYPES.CUSTOM_PALLET]: {
    item_length: '',
    item_width: '',
  },
};
const NON_PALLETIZED_SIZES = {
  [NON_PALLETIZED_TYPES.PARCEL]: {
    item_length: '',
    item_width: '',
  },
  [NON_PALLETIZED_TYPES.BULK]: {
    item_length: '',
    item_width: '',
  },
};

const baseShipperOrder = () => {
  const baseShipperOrders = {
    [TENANTS_NAMES.ontruck]: {
      [SHIPPER_ORDER_TYPE]: DISTRIBUTION,
      shipper: null,
      user: null,
      shipments: [
        baseShipment({
          loads: [baseLoad()],
          extras: [],
        }),
      ],
      standstills: [],
    },
    [TENANTS_NAMES.intraser]: {
      shipper_order_type: 'COLLECTION',
      shipper: null,
      user: null,
      shipments: [
        baseShipment({
          loads: [baseLoad()],
          extras: [],
        }),
        baseShipment({
          loads: [baseLoad()],
          extras: [],
        }),
      ],
      standstills: [],
    },
  };
  return baseShipperOrders[formConfig];
};

const baseLoad = (baseParams = {}) => {
  const baseLoads = {
    [TENANTS_NAMES.ontruck]: {
      ...PALLET_SIZES[PALLET_TYPES.EU_PALLET],
      load_type: LOAD_TYPES.PALLET,
      pallet_type: PALLET_TYPES.EU_PALLET,
      temperature_range: null,
      number_of_items: '',
      total_weight: '',
      ...baseParams,
    },
    [TENANTS_NAMES.intraser]: {
      item_length: 1,
      item_width: 1,
      item_height: 1,
      load_type: 'BULK',
      pallet_type: 'CUSTOM_PALLET',
      temperature_range: null,
      number_of_items: 1,
      total_weight: '',
      container_iso_code: '',
      ...baseParams,
    },
  };
  return baseLoads[formConfig];
};

const baseShipment = (baseParams = {}) => ({
  ...baseParams,
  dropoff_comment_from_shipper_to_driver: '',
  pickup_comment_from_shipper_to_driver: '',
  internal_reference: '',
});

const baseExtra = ({
  concept,
  type = EXTRA_NUMERIC,
  reviewed_by_shipper = false,
  amount,
  referral_shipper_order_id,
}) => ({
  type,
  concept,
  amount,
  reviewed_by_shipper,
  include_in_base: includedInBaseList.includes(concept),
  referral_shipper_order_id,
});

const isStarted = ({ status }) => ['IN_TRANSIT', 'DONE', 'DONE_WITHOUT_MANIFEST'].includes(status);

const cancellationValues = {
  DEFAULT: [0, 40, 70, 100],
  [SERVICE_TYPE_ONTRUCK24]: {
    ES: [0, 50, 50, 100],
  },
};

export {
  SHIPMENTS_FIELD,
  EXTRAS_FIELD,
  NUMBER_OF_ITEMS_FIELD,
  INSURED_FOR_FIELD,
  TOTAL_WEIGHT_FIELD,
  TEMPERATURE_RANGE_FIELD,
  LOADS_FIELD,
  LOAD_TYPE_SWITCH_FIELD,
  LOAD_TYPE_FIELD,
  PALLET_TYPE_FIELD,
  ITEM_WIDTH_FIELD,
  ITEM_LENGTH_FIELD,
  ITEM_HEIGHT_FIELD,
  INSURANCE_FIELD,
  INSURANCE_EXTRA,
  SHIPPER_ORDER_TYPE,
  STANDSTILL_FIELD,
  DAY_HOLIDAY_EXTRA,
  PRICE_INCREMENT_EXTRA,
  DISCOUNT_EXTRA,
  STANDSTILL_EXTRA,
  LIFGATE_EXTRA,
  SIDE_LOAD_EXTRA,
  TOP_LOAD_EXTRA,
  MANUAL_EXTRA,
  MANAGEMENT_FEE_EXTRA,
  TENDER_EXTRA,
  NEGOTIATED_EXTRA,
  CANCELLATION_EXTRA,
  PENALTY_EXTRA,
  BONUS_EXTRA,
  MODIFIED_PRICE_EXTRA,
  RETURN_EXTRA,
  COST_INCREMENT_EXTRA,
  HANDLING_LOADING,
  HANDLING_UNLOADING,
  HANDLING_EXTRAS,
  LOAD_TYPE_SWITCH,
  LOAD_TYPES,
  PALLET_TYPES,
  NON_PALLETIZED_TYPES,
  PALLET_SIZES,
  NON_PALLETIZED_SIZES,
  DISTRIBUTION,
  COLLECTION,
  SINGLE_SHIPMENT,
  TRIGGER_FORM_CHANGE,
  EXTRA_PERCENT,
  EXTRA_NUMERIC,
  PENDING_PAYMENT,
  PENDING_CONFIRMATION,
  PENDING_MODERATION,
  PENDING_ASSIGNMENT,
  ASSIGNED,
  IN_TRANSIT,
  CANCELLED,
  EXPIRED,
  ACTIVE,
  MIXED_MULTI_ROUTE,
  stopInfo,
  pickupStopInfo,
  dropoffStopInfo,
  baseLoad,
  baseShipment,
  baseShipperOrder,
  includedInBaseList,
  baseExtra,
  isStarted,
  cancellationValues,
};
